import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { Skeleton } from "@mui/material";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FixedSizeList as List, ListChildComponentProps } from "react-window";
import {
  selectFolders,
  selectFoldersFetched,
  selectHasUnsubittedFolder,
  selectRenamingFolder,
  selectSelectedFolder,
} from "../../app/slices/AssetsSlice";
import ContextualHint, { HintId } from "../onboarding/hints/ContextualHint";
import "./FileExplorerDialog.css";
import FolderButton from "./FolderButton";

import { AutoSizer } from "react-virtualized";
import { useAddFolder } from "../../hooks/AddFolderHook";
import "./FolderView.css";
import useFolderViewPageLoader from "./FolderViewPageLoaderHooks";

export default function FolderView() {
  const { t } = useTranslation();
  const folders = useSelector(selectFolders);
  const foldersFetched = useSelector(selectFoldersFetched);
  const selectedFolderId = useSelector(selectSelectedFolder);
  const selectCurrentRenamingFolder = useSelector(selectRenamingFolder);
  const [addFolder, addTempFolder] = useAddFolder();
  const [onItemsRendered] = useFolderViewPageLoader();
  const hasUnssubmittedFolder = useSelector(selectHasUnsubittedFolder);

  const dispatch = useDispatch();
  const listRef = useRef<List>(null);

  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

  function renderRow({ index, style }: ListChildComponentProps) {
    return folders[index].id !== "loading" ? (
      <FolderButton
        isSelected={folders[index].id === selectedFolderId?.id}
        isRenamingFolder={folders[index] === selectCurrentRenamingFolder}
        key={index}
        style={style}
        folder={folders[index]}
      />
    ) : (
      <div style={style}>
        <Skeleton variant="rounded" style={{ margin: "8px" }} />
      </div>
    );
  }

  async function onAddFolderClicked() {
    //dispatch(addNewFolder());
    //await delay(100);
    //listRef.current?.scrollToItem(folders.length);
    //await addFolder();
    addTempFolder();
  }

  return (
    <div
      className="flex flex-col pi-folderview-content-folders w-[178px] shrink-0 border-r border-creator-border bg-creator-primary-background px-[10px] py-[12px] gap-[4px]"
      data-testid="folder-view"
    >
      <div className="px-[9px] pb-[2px] text-[9.5px] font-semibold uppercase tracking-[0.08em] text-creator-text-sub">
        {t("file_explorer_ui.folders")}
      </div>

      {/*
        With zero folders the virtualized List renders itemCount={0} — a
        completely blank column above the Add Folder button, with no empty
        state explaining what folders are or that they are optional.

        Gated on foldersFetched rather than folders.length so it cannot flash
        while the fetch is still in flight; "loading" and "genuinely empty"
        are different states.
      */}
      <ContextualHint
        hintId={HintId.FileExplorerFolders}
        when={foldersFetched && folders.length === 0}
        className="shrink-0"
      />

      <div className="flex-1 min-h-0">
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <List
              className="pi-folderview-content-folders-list pit-quiet-scrollbar"
              ref={listRef}
              height={height}
              itemCount={folders.length}
              // 32px row plus the frames' 3px rail spacing
              itemSize={35}
              width={width}
              onItemsRendered={onItemsRendered}
            >
              {renderRow}
            </List>
          )}
        </AutoSizer>
      </div>
      {/*
        A quiet outlined pill, per the frames — the rail is chrome, and a
        filled blue button here competed with the footer's primary action.
      */}
      <button
        type="button"
        onClick={onAddFolderClicked}
        disabled={hasUnssubmittedFolder}
        data-testid="folder-view-add-folder-button"
        className="shrink-0 h-[33px] w-full rounded-[6px] border border-creator-border bg-creator-surface flex items-center justify-center gap-[7px] text-[11.5px] font-medium text-creator-text transition-colors hover:bg-creator-childActive disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <AddOutlinedIcon sx={{ fontSize: 12 }} />
        {t("file_explorer_ui.add_folder")}
      </button>
    </div>
  );
}
